// Fires `runUserBootstrap` once per (session.user.id, organization) when the
// auth state flips to "ready" with an active org. Publishes any fresh
// achievements to `FreshAchievementsContext` so the shell-level toast / banner
// can react.
//
// The bootstrap endpoint is idempotent — repeated calls are cheap no-ops on
// the server. We still gate per-session to avoid network noise.
import { useEffect, useRef } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import { runUserBootstrap } from "@/lib/usersBootstrapClient";

export function useUserBootstrap(): void {
  const auth = useAuthOptional();
  const fresh = useFreshAchievementsOptional();
  const ranForKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!auth || !fresh) return;
    if (auth.status !== "ready") return;
    const userId = auth.session?.user?.id ?? null;
    const orgId = auth.activeOrganizationId;
    if (!userId || !orgId) return;

    const runKey = `${userId}::${orgId}`;
    if (ranForKeyRef.current === runKey) return;
    ranForKeyRef.current = runKey;

    let cancelled = false;
    void (async () => {
      try {
        const result = await runUserBootstrap();
        if (cancelled) return;
        if (!result.ok) {
          console.warn("[user-bootstrap] failed", result.error);
          return;
        }
        if (result.fresh_achievements?.length) {
          fresh.publish(result.fresh_achievements);
        }
      } catch (err) {
        if (!cancelled) console.warn("[user-bootstrap] threw", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth, fresh]);
}

/** Component form — mount once near the root inside both AuthProvider + FreshAchievementsProvider. */
export function UserBootstrapRunner() {
  useUserBootstrap();
  return null;
}
